import React, { useState, useEffect } from 'react';
import { Table, Card, Button, Space, Tag, Modal, Input, message, Image, Row, Col } from 'antd';
import { PlusOutlined, EyeOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { faultReportService } from '../../services/api/fault-report.service';
import type { FaultReport } from '../../types/faultReport';
import { useSelector } from 'react-redux';
import { RootState } from '../../store/store';
import dayjs from 'dayjs';

const { TextArea } = Input;

const FaultReportList: React.FC = () => {
  const [reports, setReports] = useState<FaultReport[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedReport, setSelectedReport] = useState<FaultReport | null>(null);
  const [detailsVisible, setDetailsVisible] = useState(false);
  const [updateNotes, setUpdateNotes] = useState('');
  const user = useSelector((state: RootState) => state.auth.user);
  const navigate = useNavigate();

  useEffect(() => {
    fetchReports();
  }, []);

  const fetchReports = async () => {
    try {
      setLoading(true);
      const response = await faultReportService.getAll();
      setReports(response.data.data);
    } catch (error) {
      message.error('Greška pri učitavanju prijava');
    } finally {
      setLoading(false);
    }
  };

  const handleStatusUpdate = async (id: number, newStatus: 'pending' | 'in_progress' | 'resolved' | 'rejected') => {
    try {
      await faultReportService.update(id, {
        status: newStatus,
        resolution_notes: updateNotes
      });
      message.success('Status uspješno ažuriran');
      fetchReports();
      setDetailsVisible(false);
    } catch (error) {
      message.error('Greška pri ažuriranju statusa');
    }
  };

  const getStatusTag = (status: string) => {
    const statusConfig = {
      pending: { color: 'gold', text: 'Na čekanju' },
      in_progress: { color: 'blue', text: 'U obradi' },
      resolved: { color: 'green', text: 'Riješeno' }
    };
    const config = statusConfig[status as keyof typeof statusConfig];
    return <Tag color={config.color}>{config.text}</Tag>;
  };

  const getFaultTypeText = (type: string) => {
    const types: Record<string, string> = {
      'leak': 'Curenje vode',
      'meter_damage': 'Oštećenje vodomjera',
      'low_pressure': 'Slab pritisak vode',
      'quality': 'Problem sa kvalitetom vode',
      'connection': 'Problem sa priključkom',
      'other': 'Ostalo'
    };
    return types[type] || type;
  };

  const columns = [
    {
      title: 'Datum prijave',
      dataIndex: 'created_at',
      key: 'created_at',
      render: (date: string) => dayjs(date).format('DD.MM.YYYY HH:mm')
    },
    {
      title: 'Tip kvara',
      dataIndex: 'fault_type',
      key: 'fault_type',
      render: (type: string) => getFaultTypeText(type)
    },
    {
      title: 'Adresa',
      dataIndex: 'address',
      key: 'address',
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status: string) => getStatusTag(status)
    },
    {
      title: 'Akcije',
      key: 'actions',
      render: (_: any, record: FaultReport) => (
        <Space>
          <Button
            type="primary"
            icon={<EyeOutlined />}
            onClick={() => {
              setSelectedReport(record);
              setDetailsVisible(true);
            }}
          >
            Detalji
          </Button>
        </Space>
      ),
    },
  ];

  const DetailsModal = () => {
    if (!selectedReport) return null;

    return (
      <Modal
        title="Detalji prijave"
        open={detailsVisible}
        onCancel={() => setDetailsVisible(false)}
        footer={[
          <Button key="close" onClick={() => setDetailsVisible(false)}>
            Zatvori
          </Button>,
          user?.role === 'administrator' && selectedReport.status !== 'resolved' && (
            <Button
              key="resolve"
              type="primary"
              onClick={() => handleStatusUpdate(selectedReport.id, 'resolved')}
            >
              Označi kao riješeno
            </Button>
          ),
        ]}
        width={800}
      >
        <Row gutter={[16, 16]}>
          <Col span={12}>
            <p><strong>Podnosilac:</strong> {selectedReport.name}</p>
            <p><strong>Email:</strong> {selectedReport.email}</p>
            <p><strong>Telefon:</strong> {selectedReport.phone}</p>
            <p><strong>Tip kvara:</strong> {getFaultTypeText(selectedReport.fault_type)}</p>
            <p><strong>Status:</strong> {selectedReport.status ? getStatusTag(selectedReport.status) : 'N/A'}</p>
          </Col>
          <Col span={12}>
            <p><strong>Adresa:</strong> {selectedReport.address}</p>
            <p><strong>Dodatne informacije o lokaciji:</strong> {selectedReport.location_details}</p>
            <p><strong>Datum prijave:</strong> {dayjs(selectedReport.created_at).format('DD.MM.YYYY HH:mm')}</p>
          </Col>
        </Row>

        <div className="mt-4">
          <p><strong>Opis problema:</strong></p>
          <p>{selectedReport.description}</p>
        </div>

        {selectedReport.resolution_notes && (
          <div className="mt-4">
            <p><strong>Napomene o rješenju:</strong></p>
            <p>{selectedReport.resolution_notes}</p>
          </div>
        )}

        {user?.role === 'administrator' && selectedReport.status !== 'resolved' && (
          <div className="mt-4">
            <p><strong>Dodaj napomenu o rješenju:</strong></p>
            <TextArea
              rows={4}
              value={updateNotes}
              onChange={(e) => setUpdateNotes(e.target.value)}
              placeholder="Unesite napomenu o rješenju..."
            />
          </div>
        )}

        {selectedReport.images && selectedReport.images.length > 0 && (
          <div className="mt-4">
            <p><strong>Slike:</strong></p>
            <Image.PreviewGroup>
              <Row gutter={[8, 8]}>
                {selectedReport.images.map((image) => (
                  <Col key={image.id} span={8}>
                    <Image
                      src={`${process.env.REACT_APP_API_URL}/storage/${image.image_path}`}
                      alt="Slika kvara"
                      style={{ width: '100%', height: '150px', objectFit: 'cover' }}
                    />
                  </Col>
                ))}
              </Row>
            </Image.PreviewGroup>
          </div>
        )}
      </Modal>
    );
  };

  return (
    <div className="p-6">
      <Card
        title="Prijave kvarova"
        extra={
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => navigate('/issues/new')}
          >
            Nova prijava
          </Button>
        }
      >
        <Table
          columns={columns}
          dataSource={reports}
          rowKey="id"
          loading={loading}
        />
        <DetailsModal />
      </Card>
    </div>
  );
};

export default FaultReportList;